import { useCallback, useMemo, useState } from "react";

import {
  RiskService,
  type RiskDimension,
  type RiskSignalsFilters,
  type SignalEventListResponse,
  type SignalEventResponse,
  type SignalSeverity,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID } from "@/lib/format";
import { DIMENSION_KEY } from "@/components/risk/risk-dimension-keys";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FilterChips } from "@/components/shared/filter-chip";
import { Pagination } from "@/components/shared/pagination";
import { SignalSeverityBadge } from "@/components/shared/signal-badge";
import { RiskSignalDetailDialog } from "./risk-signal-detail-dialog";

const PAGE_SIZE = 25;

// Signal severities map to translation keys so the filter chips
// re-render on a locale switch.
const SEVERITY_KEY: Record<SignalSeverity, TranslationKey> = {
  low: "risk.severity.low",
  medium: "risk.severity.medium",
  high: "risk.severity.high",
  critical: "risk.severity.critical",
};

export function RiskSignalsTab() {
  const { t } = useTranslation();
  const [severity, setSeverity] = useState<SignalSeverity | null>(null);
  const [dimension, setDimension] = useState<RiskDimension | null>(null);
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<SignalEventResponse | null>(null);

  const offset = page * PAGE_SIZE;

  const fetcher = useCallback(() => {
    const filters: RiskSignalsFilters = {
      limit: PAGE_SIZE,
      offset,
    };
    if (severity) filters.severity = severity;
    if (dimension) filters.dimension = dimension;
    return RiskService.listSignals(filters);
  }, [severity, dimension, offset]);

  const { data, error, isLoading } = useApi<SignalEventListResponse>(fetcher, {
    deps: [severity, dimension, offset],
  });

  // Resetting offset on filter change keeps the visible page in
  // sync with the new dataset — otherwise the user can land on
  // "page 5" of a 10-row narrow filter and see nothing.
  const handleSeverity = (next: SignalSeverity | null) => {
    setSeverity(next);
    setPage(0);
  };
  const handleDimension = (next: RiskDimension | null) => {
    setDimension(next);
    setPage(0);
  };

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;

  const severityOptions = useMemo(
    () =>
      (["low", "medium", "high", "critical"] as const).map((s) => ({
        value: s,
        label: t(SEVERITY_KEY[s]),
      })),
    [t]
  );
  const dimensionOptions = useMemo(
    () =>
      (["security", "policy", "asset", "compliance"] as const).map((d) => ({
        value: d,
        label: t(DIMENSION_KEY[d]),
      })),
    [t]
  );

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

      <div className="flex flex-col gap-2 tablet:flex-row tablet:items-center">
        <FilterChips
          label={t("risk.signals.filterSeverity")}
          options={severityOptions}
          value={severity}
          onChange={handleSeverity}
        />
        <FilterChips
          label={t("risk.signals.filterDimension")}
          options={dimensionOptions}
          value={dimension}
          onChange={handleDimension}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("risk.signals.colTime")}</TableHead>
              <TableHead>{t("risk.signals.colType")}</TableHead>
              <TableHead>{t("risk.signals.colSeverity")}</TableHead>
              <TableHead>{t("risk.signals.colDimension")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("risk.signals.colSource")}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("risk.signals.colValue")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={`sig-sk-${i}`}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && items.length === 0 && (
              <TableEmpty colSpan={6} message={t("risk.signals.empty")} />
            )}
            {items.map((sig) => (
              <TableRow
                key={sig.id}
                onClick={() => setDetail(sig)}
                className="cursor-pointer"
              >
                <TableCell className="whitespace-nowrap text-neutral-700">
                  {formatDateID(sig.created_at)}
                </TableCell>
                <TableCell className="font-medium text-ocean-900">
                  {sig.signal_type}
                </TableCell>
                <TableCell>
                  <SignalSeverityBadge severity={sig.severity} />
                </TableCell>
                <TableCell>
                  {DIMENSION_KEY[sig.dimension_affected]
                    ? t(DIMENSION_KEY[sig.dimension_affected])
                    : sig.dimension_affected}
                </TableCell>
                <TableCell className="hidden tablet:table-cell text-neutral-600">
                  {sig.signal_source}
                </TableCell>
                <TableCell className="hidden tablet:table-cell text-neutral-600">
                  {sig.signal_value ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {items.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          hasNext={hasNext}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}

      <RiskSignalDetailDialog
        signal={detail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
